import { AxiosResponse } from 'axios';
import { createApiClient } from './axios-instance';
import {
  CreditPackage,
  CheckoutRequest,
  CheckoutResponse,
  TransactionListResponse,
  TransactionHistoryFilters,
  TransactionResponse,
  CreditTransactionType,
} from '../types/payment.types';

/**
 * Payment service for handling payment-related API calls
 */
export class PaymentService {
  /**
   * Get all available credit packages
   */
  static async getCreditPackages(idToken: string): Promise<CreditPackage[]> {
    const client = createApiClient(idToken);
    const response: AxiosResponse<{ data: CreditPackage[] }> =
      await client.get('/payments/packages');

    return response.data.data;
  }

  /**
   * Create checkout link for workspace
   */
  static async createWorkspaceCheckout(
    idToken: string,
    workspaceId: string,
    request: CheckoutRequest
  ): Promise<CheckoutResponse> {
    const client = createApiClient(idToken);
    const response: AxiosResponse<{ data: CheckoutResponse }> = await client.post(
      `/payments/workspaces/${workspaceId}/checkout`,
      request,
      {
        headers: {
          'X-Workspace-ID': workspaceId,
        },
      }
    );
    return response.data.data;
  }

  /**
   * Get workspace transaction history
   */
  static async getWorkspaceTransactions(
    idToken: string,
    workspaceId: string,
    params: {
      page?: number;
      page_size?: number;
      filters?: TransactionHistoryFilters;
    } = {}
  ): Promise<TransactionListResponse> {
    const client = createApiClient(idToken);
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', params.page.toString());
    if (params.page_size) queryParams.set('page_size', params.page_size.toString());
    if (params.filters?.type) queryParams.set('type', params.filters.type);
    if (params.filters?.start_date) queryParams.set('start_date', params.filters.start_date);
    if (params.filters?.end_date) queryParams.set('end_date', params.filters.end_date);

    const response: AxiosResponse<{
      data: TransactionResponse[];
      total_count: number;
      page: number;
      page_size: number;
    }> = await client.get(`/workspaces/${workspaceId}/transactions?${queryParams.toString()}`, {
      headers: {
        'X-Workspace-ID': workspaceId,
      },
    });

    // Transform flat TransactionResponse to CreditTransaction format
    const transactions = response.data.data.map((tx) => ({
      id: tx.id,
      workspace_id: workspaceId,
      transaction_type: tx.transaction_type as CreditTransactionType,
      credits_change: tx.credits_change,
      transaction_metadata: tx.transaction_metadata,
      payment_order_id: null, // Not included in TransactionResponse
      created_at: tx.created_at,
    }));

    return {
      transactions,
      total_count: response.data.total_count,
      page: response.data.page,
      page_size: response.data.page_size,
    };
  }

  /**
   * Handle checkout redirect and process return
   */
  static handleCheckoutReturn(): void {
    // This would typically check URL parameters for success/failure
    // and trigger credit balance refresh
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('checkout_status');

    if (status === 'success') {
      // Trigger credit refresh in the application
      window.dispatchEvent(new CustomEvent('credits-updated'));
    }
  }

  /**
   * Format price from cents to display format
   */
  static formatPrice(cents: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(cents / 100);
  }

  /**
   * Calculate credits per dollar value for comparison
   */
  static calculateCreditsPerDollar(credits: number, priceCents: number): number {
    return credits / (priceCents / 100);
  }

  /**
   * Find the most popular package based on value
   */
  static findMostPopularPackage(packages: CreditPackage[] | undefined): number | null {
    if (!packages || packages.length === 0) {
      return null;
    }

    // For now, return the first active variant ID
    // TODO: Implement proper value calculation when pricing info is available
    for (const pkg of packages) {
      if (pkg.variants && pkg.variants.length > 0) {
        return pkg.variants[0].id;
      }
    }

    return null;
  }

  /**
   * Open checkout in new tab
   */
  static openCheckout(checkoutUrl: string): Window | null {
    // Open blank window first to prevent popup blockers
    const newWindow = window.open(
      '',
      '_blank',
      'width=800,height=600,scrollbars=yes,resizable=yes'
    );

    if (!newWindow) {
      // Show error instead of redirect
      alert('Please allow popups for this site to complete your purchase');
      throw new Error('Popup blocked');
    }

    // Set the URL after opening
    newWindow.location.href = checkoutUrl;
    return newWindow;
  }

  /**
   * Get checkout customer info from user profile
   */
  static getCustomerEmail(userProfile: any): string | undefined {
    return userProfile?.email;
  }
}
